// Client Care Command Centre — Individual client profile.
// Premium relationship history with score dial, health pill, sectioned overview,
// polished timeline rail, opportunities, and quick actions.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Loader2, PhoneCall, Mail, MessageSquare, User, MapPin,
  DollarSign, Calendar, Building2, FileText, Zap, Sparkles,
  CheckCircle2, Clock, Star, TrendingUp, HeartHandshake,
  CalendarClock, Video, ChevronDown, ChevronUp, Radar, Award,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { QuickNoteModal, BrokerHandoffModal } from '../components/post_settlement/Modals';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

// ─── Helpers ─────────────────────────────────────────────────────
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; } };
const fmtDateTime = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
const fmtRel = (iso) => { if (!iso) return '—'; try { const d = new Date(iso); const diff = Math.round((Date.now() - d.getTime()) / 86400000); if (diff === 0) return 'today'; if (diff === 1) return 'yesterday'; if (diff > 1) return `${diff}d ago`; if (diff === -1) return 'tomorrow'; return `in ${-diff}d`; } catch { return iso; } };
const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;
const daysSince = (iso) => { if (!iso) return null; try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return null; } };
const daysUntil = (iso) => { if (!iso) return null; try { return Math.floor((new Date(iso).getTime() - Date.now()) / 86400000); } catch { return null; } };

// Care Score & health status (same rules as list view)
const computeCareScore = (c) => {
  const contactedRecent = c?.last_activity?.created_at || c?.last_contact_at;
  const ds = daysSince(contactedRecent);
  const contactedOK = ds !== null && ds <= 90;
  const nextDue = c?.next_task?.scheduled_at ? daysUntil(c.next_task.scheduled_at) : null;
  const noOverdue = nextDue === null || nextDue >= 0;
  const npsCat = c?.latest_nps?.category || c?.last_nps_category;
  const npsScore = npsCat === 'Promoter' ? 20 : npsCat === 'Passive' ? 10 : 0;
  const hasOpp = (c?.open_opps || []).length > 0;
  const referral = (c?.open_opps || []).some((o) => (o.opportunity_type || o.type) === 'referral');
  return (contactedOK ? 30 : 0) + (noOverdue ? 25 : 0) + npsScore + (hasOpp ? 15 : 0) + (referral ? 10 : 0);
};
const HEALTH_META = {
  healthy:         { label: 'Healthy',         cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60', dot: 'bg-emerald-500' },
  needs_attention: { label: 'Needs attention', cls: 'bg-amber-50  text-amber-800  ring-1 ring-amber-200/60',   dot: 'bg-amber-500'   },
  overdue:         { label: 'Overdue',         cls: 'bg-rose-50   text-rose-700   ring-1 ring-rose-200/60',    dot: 'bg-rose-500'    },
  opportunity:     { label: 'Opportunity',     cls: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200/60',  dot: 'bg-violet-500'  },
  broker_review:   { label: 'Broker review',   cls: 'bg-blue-50   text-blue-700   ring-1 ring-blue-200/60',    dot: 'bg-blue-500'    },
  at_risk:         { label: 'At risk',         cls: 'bg-red-50    text-red-700    ring-1 ring-red-200/60',     dot: 'bg-red-500'     },
  dnc:             { label: 'Do not contact',  cls: 'bg-slate-100 text-slate-500  ring-1 ring-slate-200',      dot: 'bg-slate-400'   },
};
const computeHealth = (c) => {
  if (c?.suppression_dnc || c?.do_not_contact) return 'dnc';
  const derived = c?.derived_status;
  if (['rate_review', 'refinance', 'equity', 'new_purchase'].includes(derived)) return 'opportunity';
  if (derived === 'broker_review') return 'broker_review';
  if (derived === 'lost_contact') return 'at_risk';
  const nps = c?.latest_nps?.category || c?.last_nps_category;
  if (nps === 'Detractor') return 'at_risk';
  const dueDays = c?.next_task?.scheduled_at ? daysUntil(c.next_task.scheduled_at) : null;
  if (dueDays !== null && dueDays < 0) return 'overdue';
  if (dueDays !== null && dueDays <= 3) return 'needs_attention';
  if (derived === 'first_checkin_due') return 'needs_attention';
  return 'healthy';
};

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════
export default function PostSettlementClientProfile() {
  const { loan_id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNote, setShowNote] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: t }] = await Promise.all([
        axios.get(`${API}/api/post-settlement/clients/${loan_id}`, { headers: authHeaders }),
        axios.get(`${API}/api/post-settlement/clients/${loan_id}/timeline`, { headers: authHeaders }),
      ]);
      setClient(c); setTimeline(t.events || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load client'); }
    finally { setLoading(false); }
  }, [loan_id, authHeaders]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)]">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-16 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </main>
      </div>
    );
  }
  if (!client) return null;
  const c = client;
  const health = computeHealth(c);
  const meta = HEALTH_META[health];
  const score = computeCareScore(c);
  const scoreTone = score >= 80 ? { ring: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Excellent' } :
                    score >= 50 ? { ring: '#f59e0b', bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Watch' } :
                                  { ring: '#ef4444', bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'At risk' };

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button type="button" onClick={() => navigate('/post-settlement')} data-testid="ps-back-hub"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Command Centre
        </button>

        {/* ═══ HERO ═══ */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="relative overflow-hidden rounded-3xl bg-white border border-slate-200/80 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.06)] p-6 sm:p-8">
          {/* Ambient background glow */}
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: `radial-gradient(circle, ${scoreTone.ring} 0%, transparent 70%)` }} />

          <div className="relative flex flex-col lg:flex-row gap-6 lg:items-center justify-between">
            {/* Left — identity */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  <User className="w-3 h-3" /> Client Care Profile
                </span>
                <span className="text-[10px] font-mono text-slate-400">{c.loan_number || '—'}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-950 leading-[1.05]">
                {c.client_name}
              </h1>
              {c.partner_name && <p className="text-base text-slate-500 mt-1">with {c.partner_name}</p>}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span data-testid="ps-health-status"
                  className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full ${meta.cls}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                </span>
                {c.derived_status_label && c.derived_status !== health && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                    <Sparkles className="w-3 h-3" /> {c.derived_status_label}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <Calendar className="w-3 h-3" /> Settled {fmtDate(c.settlement_date)}
                </span>
              </div>

              {/* Contact buttons */}
              <div className="mt-5 flex flex-wrap gap-2">
                {c.mobile_number && (
                  <a href={`tel:${c.mobile_number}`} data-testid="ps-call"
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3.5 py-2 text-[12px] font-bold transition-colors">
                    <PhoneCall className="w-3.5 h-3.5" /> Call
                  </a>
                )}
                {c.mobile_number && (
                  <a href={`sms:${c.mobile_number}`} data-testid="ps-sms"
                    className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100 px-3.5 py-2 text-[12px] font-bold transition-colors">
                    <MessageSquare className="w-3.5 h-3.5" /> SMS
                  </a>
                )}
                {c.email && (
                  <a href={`mailto:${c.email}`} data-testid="ps-email"
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100 px-3.5 py-2 text-[12px] font-bold transition-colors">
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                )}
                <button type="button" onClick={() => setShowNote(true)} data-testid="ps-quick-note"
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 px-4 py-2 text-[12px] font-bold transition-colors shadow-sm">
                  <Zap className="w-3.5 h-3.5" /> Log check-in
                </button>
                <button type="button" onClick={() => setShowHandoff(true)} data-testid="ps-broker-handoff"
                  className="inline-flex items-center gap-1.5 rounded-full text-white px-4 py-2 text-[12px] font-bold transition-colors shadow-sm"
                  style={{ background: 'linear-gradient(135deg,#0057FF 0%,#0044cc 100%)' }}>
                  <HeartHandshake className="w-3.5 h-3.5" /> Send to broker
                </button>
              </div>
            </div>

            {/* Right — Care Score gauge */}
            <div className="flex flex-col items-center lg:items-end gap-2 flex-shrink-0" data-testid="ps-care-score">
              <CareScoreGauge score={score} tone={scoreTone} size={140} />
              <p className={`text-[11px] font-black uppercase tracking-[0.2em] ${scoreTone.text}`}>{scoreTone.label}</p>
              <p className="text-[11px] text-slate-500 text-center max-w-[180px] leading-snug">Client Care Score reflects contact recency, opportunities, NPS &amp; overdue tasks.</p>
            </div>
          </div>

          {/* Aggregate stat strip */}
          <div className="relative mt-6 pt-5 border-t border-slate-100 grid grid-cols-3 sm:grid-cols-6 gap-3">
            <MiniStat label="Activities"      value={c.activity_count} />
            <MiniStat label="Care done"       value={c.care_completed} tone="text-emerald-700" />
            <MiniStat label="Care pending"    value={c.care_pending}   tone={c.care_pending > 0 ? 'text-amber-700' : 'text-slate-700'} />
            <MiniStat label="Open opps"       value={(c.open_opps || []).length} tone="text-violet-700" />
            <MiniStat label="Latest NPS"      value={c.latest_nps ? `${c.latest_nps.score}/10` : '—'} tone={c.latest_nps?.category === 'Detractor' ? 'text-rose-700' : c.latest_nps?.category === 'Promoter' ? 'text-emerald-700' : 'text-slate-700'} />
            <MiniStat label="Days since settle" value={daysSince(c.settlement_date) ?? '—'} />
          </div>
        </motion.section>

        {/* ═══ MAIN GRID ═══ */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column — Loan / Property / Team */}
          <div className="lg:col-span-1 space-y-4">
            <InfoCard icon={DollarSign} eyebrow="Loan snapshot" title="Loan details">
              <Row label="Amount"     value={fmtMoney(c.loan_amount)} />
              <Row label="Product"    value={c.product_type} />
              <Row label="Purpose"    value={c.loan_purpose} />
              <Row label="Repayment"  value={c.repayment_type} />
              <Row label="Rate"       value={c.interest_rate ? `${c.interest_rate}% ${c.rate_type || ''}`.trim() : (c.rate_type || '—')} />
              <Row label="Term"       value={c.loan_term_years ? `${c.loan_term_years} yrs` : '—'} />
              {c.fixed_rate_expiry && <Row label="Fixed expiry" value={fmtDate(c.fixed_rate_expiry)} highlight />}
            </InfoCard>

            <InfoCard icon={Building2} eyebrow="Lender" title={c.lender_name || c.lender || '—'}>
              <Row label="Product tier" value={c.product_tier} />
              <Row label="Offset"       value={c.has_offset ? `Yes${c.offset_balance ? ` · ${fmtMoney(c.offset_balance)}` : ''}` : 'No'} />
              <Row label="Redraw"       value={c.has_redraw ? 'Yes' : 'No'} />
              <Row label="Originated by Derwent" value={c.originated_by_derwent ? 'Yes' : 'No'} />
              {c.clawback_end_date && <Row label="Clawback until" value={fmtDate(c.clawback_end_date)} />}
            </InfoCard>

            <InfoCard icon={MapPin} eyebrow="Property" title={c.property_address || c.security_address || '—'}>
              <Row label="State"     value={c.property_state} />
              <Row label="At settle" value={c.property_value_at_settlement ? fmtMoney(c.property_value_at_settlement) : '—'} />
            </InfoCard>

            <InfoCard icon={User} eyebrow="Team" title="Assigned">
              <Row label="Broker"          value={c.broker_name || c.created_by_name} />
              <Row label="Post-settlement" value={c.assigned_officer_name || '—'} />
              <Row label="Referral source" value={c.referral_source} />
            </InfoCard>
          </div>

          {/* Middle column — Next touchpoint + Opportunities */}
          <div className="lg:col-span-1 space-y-4">
            <InfoCard icon={CalendarClock} eyebrow="Next touchpoint" title={c.next_task ? c.next_task.title : 'No pending follow-ups'}>
              {c.next_task ? (
                <>
                  <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-white ring-1 ring-blue-100 p-4 mt-1">
                    <p className="text-[11px] font-black uppercase tracking-widest text-blue-700">Scheduled</p>
                    <p className="text-lg font-black text-slate-900 mt-1">{fmtDate(c.next_task.scheduled_at)}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{fmtRel(c.next_task.scheduled_at)} · {c.next_task.type || 'email'}</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500 mt-2">No pending follow-ups. Log a check-in to schedule the next one.</p>
              )}
              {c.latest_nps && (
                <div className="mt-3 rounded-2xl border border-slate-200/60 p-3.5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Latest NPS</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className={`text-2xl font-black ${c.latest_nps.category === 'Detractor' ? 'text-rose-700' : c.latest_nps.category === 'Promoter' ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {c.latest_nps.score}/10
                    </span>
                    <span className="text-[11px] font-bold text-slate-500">{c.latest_nps.category}</span>
                  </div>
                  {c.latest_nps.comment && <p className="text-[12px] text-slate-600 mt-1 leading-snug">&ldquo;{c.latest_nps.comment}&rdquo;</p>}
                </div>
              )}
            </InfoCard>

            <InfoCard icon={Radar} eyebrow="Opportunities" title={`${(c.open_opps || []).length} open`}>
              {(c.open_opps || []).length === 0 ? (
                <p className="text-sm text-slate-500 mt-1">No open opportunities on this file.</p>
              ) : (
                <div className="space-y-2 mt-1">
                  {c.open_opps.map((o) => {
                    const label = ((o.opportunity_type || o.type) || '').replace(/_/g, ' ');
                    return (
                      <div key={o.id} className="rounded-2xl p-3 bg-violet-50/50 ring-1 ring-violet-100">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-black text-slate-900 capitalize">{label}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">Stage: {o.stage || 'New'}{(o.estimated_amount || o.value) ? ` · ${fmtMoney(o.estimated_amount || o.value)}` : ''}</p>
                          </div>
                          <Sparkles className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                        </div>
                        {o.notes && <p className="text-[12px] text-slate-600 mt-2 leading-snug">{o.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </InfoCard>

            {(c.mobile_number || c.email) && (
              <InfoCard icon={PhoneCall} eyebrow="Contact" title="Reach the client">
                {c.mobile_number && <Row label="Mobile" value={c.mobile_number} mono />}
                {c.email && <Row label="Email" value={c.email} />}
              </InfoCard>
            )}
          </div>

          {/* Right column — Timeline */}
          <div className="lg:col-span-1">
            <div data-testid="ps-timeline"
              className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] p-5 sm:p-6 h-full">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Relationship Timeline</span>
              </div>
              <p className="text-lg font-black text-slate-900 mb-4">Every interaction, in order</p>
              {timeline.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-6 text-center">
                  <p className="text-sm text-slate-600">No events yet.</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Log a check-in to start building the timeline.</p>
                </div>
              ) : (
                <ol className="relative pl-5 border-l-2 border-dashed border-slate-200">
                  {timeline.slice(0, 100).map((e, i) => (
                    <TimelineItem key={i} event={e} idx={i} />
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <AnimatePresence>
        {showNote && <QuickNoteModal loanId={loan_id} authHeaders={authHeaders} onClose={() => setShowNote(false)} onSaved={() => { setShowNote(false); load(); }} />}
        {showHandoff && <BrokerHandoffModal loanId={loan_id} authHeaders={authHeaders} onClose={() => setShowHandoff(false)} onSaved={() => { setShowHandoff(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

// ─── Care Score gauge ───────────────────────────────────────────
const CareScoreGauge = ({ score, tone, size = 140 }) => {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, score)) / 100) * circ;
  return (
    <div className={`relative rounded-full ${tone.bg}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(15,23,42,0.06)" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={tone.ring} strokeWidth={stroke} fill="none"
                       strokeLinecap="round" strokeDasharray={circ}
                       initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
                       transition={{ duration: 0.9, ease: 'easeOut' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-black tracking-tight ${tone.text}`}>{score}</span>
        <span className={`text-[9px] font-black uppercase tracking-[0.2em] ${tone.text} mt-1`}>Care Score</span>
      </div>
    </div>
  );
};

// ─── Compact info card ──────────────────────────────────────────
const InfoCard = ({ icon: Icon, eyebrow, title, children }) => (
  <div className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] p-5">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{eyebrow}</span>
    </div>
    <p className="text-base font-black text-slate-900 leading-tight">{title}</p>
    <div className="mt-3 space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, mono, highlight }) => (
  <div className="flex items-center justify-between text-[13px] py-1">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-900 text-right ${mono ? 'font-mono' : 'font-semibold'} ${highlight ? 'text-amber-700 font-black' : ''}`}>{value || '—'}</span>
  </div>
);

const MiniStat = ({ label, value, tone = 'text-slate-800' }) => (
  <div>
    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className={`text-lg sm:text-xl font-black tracking-tight mt-0.5 ${tone}`}>{value}</p>
  </div>
);

// ─── Timeline item ──────────────────────────────────────────────
const TIMELINE_ICON = {
  settlement:         { Ic: Star,          tone: 'text-amber-700',    bg: 'bg-amber-100',    ring: 'ring-amber-200',    label: 'Settled' },
  activity_call:      { Ic: PhoneCall,     tone: 'text-emerald-700',  bg: 'bg-emerald-50',   ring: 'ring-emerald-200',  label: 'Call' },
  activity_sms:       { Ic: MessageSquare, tone: 'text-violet-700',   bg: 'bg-violet-50',    ring: 'ring-violet-200',   label: 'SMS' },
  activity_email:     { Ic: Mail,          tone: 'text-blue-700',     bg: 'bg-blue-50',      ring: 'ring-blue-200',     label: 'Email' },
  activity_video:     { Ic: Video,         tone: 'text-cyan-700',     bg: 'bg-cyan-50',      ring: 'ring-cyan-200',     label: 'Video call' },
  activity_note:      { Ic: FileText,      tone: 'text-slate-600',    bg: 'bg-slate-100',    ring: 'ring-slate-200',    label: 'Note' },
  activity_system:    { Ic: Sparkles,      tone: 'text-slate-600',    bg: 'bg-slate-100',    ring: 'ring-slate-200',    label: 'System' },
  task_completed:     { Ic: CheckCircle2,  tone: 'text-emerald-700',  bg: 'bg-emerald-50',   ring: 'ring-emerald-200',  label: 'Completed' },
  task_scheduled:     { Ic: Clock,         tone: 'text-blue-700',     bg: 'bg-blue-50',      ring: 'ring-blue-200',     label: 'Scheduled' },
  task_missed:        { Ic: Clock,         tone: 'text-rose-700',     bg: 'bg-rose-50',      ring: 'ring-rose-200',     label: 'Missed' },
  opportunity_created:{ Ic: TrendingUp,    tone: 'text-violet-700',   bg: 'bg-violet-50',    ring: 'ring-violet-200',   label: 'Opportunity' },
  nps:                { Ic: Award,         tone: 'text-amber-700',    bg: 'bg-amber-50',     ring: 'ring-amber-200',    label: 'NPS' },
};
const TimelineItem = ({ event, idx }) => {
  const [open, setOpen] = useState(idx < 3);
  const meta = TIMELINE_ICON[event.kind] || TIMELINE_ICON.activity_note;
  const hasDetail = !!event.detail;
  return (
    <motion.li initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03, duration: 0.25 }}
      className="relative pl-6 pb-4 last:pb-0">
      <span className={`absolute -left-[15px] top-0 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-white ${meta.bg}`}>
        <meta.Ic className={`w-3 h-3 ${meta.tone}`} strokeWidth={2.4} />
      </span>
      <div className={`rounded-2xl bg-slate-50/50 hover:bg-white ring-1 ring-slate-200/60 hover:${meta.ring} p-3 transition-all cursor-pointer`}
           onClick={() => hasDetail && setOpen(!open)}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[9px] font-black uppercase tracking-widest ${meta.tone}`}>{meta.label}</span>
              <span className="text-[10px] text-slate-400">{fmtRel(event.at)}</span>
            </div>
            <p className="text-[13px] font-bold text-slate-900 mt-0.5 leading-tight">{event.title}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{event.actor} · {fmtDateTime(event.at)}</p>
          </div>
          {hasDetail && (open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />)}
        </div>
        <AnimatePresence>
          {hasDetail && open && (
            <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="text-[12px] text-slate-700 mt-2 whitespace-pre-wrap leading-snug overflow-hidden">
              {event.detail}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  );
};

